/**
 * Offline Action Queue — Story X1.3.3 (PRD X1 §FR-X1-9).
 *
 * Fila GENÉRICA de ações de escrita persistida em IndexedDB e drenada na
 * THREAD PRINCIPAL ao reconectar (`online` + `visibilitychange`). NÃO depende
 * do SyncManager do Service Worker (Chromium-only) — por isso funciona em iOS
 * Safari e sobre HTTP simples (sem secure-context).
 *
 * Substitui o caminho "outgoing" órfão de `chatOfflineStore` (Story 2.7.3),
 * cujo dreno via `public/service-worker.js` foi removido no X1.1.2.
 *
 * Design: o lib é PURO (não importa axios nem React Query). Quem chama o
 * `flush()` injeta um mapa de handlers — cada handler sabe fazer a chamada de
 * rede e reconciliar o cache da sua feature. Mantém o lib testável só com
 * `fake-indexeddb`.
 */

export const OFFLINE_QUEUE_DB_NAME = "seweb-offline-queue";
export const OFFLINE_QUEUE_DB_VERSION = 1;
export const STORE_ACTIONS = "actions";

export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 60_000;
/**
 * Após este nº de falhas retryable, a ação é descartada (dead-letter). Sem isso,
 * uma ação que falha pra sempre (bug 5xx permanente) ficaria eterna na fila —
 * e, com a barreira FIFO por-tipo do flush, bloquearia todas as ações do mesmo
 * tipo indefinidamente. 8 ≈ várias horas de retry (backoff satura em 60s).
 */
export const MAX_FLUSH_ATTEMPTS = 8;

export type OfflineActionType = "send_message" | "mark_read" | "mark_all_read" | "react_feed_post";

export interface OfflineAction<P = unknown> {
  /** UUID v4 estável across retries. Vai como `Idempotency-Key` no send_message. */
  id: string;
  type: OfflineActionType;
  payload: P;
  /** Tentativas JÁ falhadas (0 = ainda não tentou / primeira falha pendente). */
  attempt: number;
  /** Epoch ms — só elegível pro flush quando `next_retry_at <= now`. */
  next_retry_at: number;
  enqueued_at: number;
}

/**
 * Handler de um tipo de ação. Resolve = sucesso (item removido da fila).
 * Lança `PermanentActionError` = descarta (4xx não-retryable). Qualquer outro
 * throw = retry com backoff (5xx/429/erro de rede).
 */
export type ActionHandler<P = unknown> = (payload: P, action: OfflineAction<P>) => Promise<void>;

export type HandlerMap = Partial<Record<OfflineActionType, ActionHandler<any>>>;

/**
 * O servidor rejeitou de forma não-retryable (4xx ≠ 429). A ação é
 * DESCARTADA da fila — re-tentar não adianta.
 */
export class PermanentActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PermanentActionError";
  }
}

// ─── Pub/sub leve (UI/contexts reagem a enqueue + flush sem polling pesado) ──

type Listener = () => void;
const listeners = new Set<Listener>();

/** Lock de re-entrância do flush (a nível de lib, não só do hook consumidor). */
let flushing = false;

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const l of listeners) {
    try {
      l();
    } catch (err) {
      console.debug("[offline-queue] listener falhou:", err);
    }
  }
}

// ─── IndexedDB helpers ───────────────────────────────────────────────────────

function isIndexedDBAvailable(): boolean {
  return typeof globalThis !== "undefined" && "indexedDB" in globalThis;
}

function generateId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }
  const rand = Math.random().toString(36).slice(2);
  return `local-${Date.now()}-${rand}`;
}

/**
 * `enqueued_at` ESTRITAMENTE monotônico por sessão. `Date.now()` pode repetir
 * em dois enqueues no mesmo ms → empate quebraria a ordem FIFO (crítico p/
 * mensagens). Bumpamos +1 no empate; após reload, `Date.now()` já avançou bem
 * além do contador, então a monotonicidade cross-sessão se mantém.
 */
let lastEnqueuedAt = 0;
function nextEnqueuedAt(): number {
  const now = Date.now();
  lastEnqueuedAt = now > lastEnqueuedAt ? now : lastEnqueuedAt + 1;
  return lastEnqueuedAt;
}

function openDb(): Promise<IDBDatabase> {
  if (!isIndexedDBAvailable()) {
    return Promise.reject(new Error("IndexedDB indisponível neste ambiente"));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(OFFLINE_QUEUE_DB_NAME, OFFLINE_QUEUE_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ACTIONS)) {
        const store = db.createObjectStore(STORE_ACTIONS, { keyPath: "id" });
        store.createIndex("by-retry", "next_retry_at", { unique: false });
        store.createIndex("by-enqueued", "enqueued_at", { unique: false });
      }
    };
    // Outra aba segurando uma versão antiga aberta bloquearia o upgrade — sem
    // este handler a Promise penduraria pra sempre. Rejeitamos pra falhar rápido.
    req.onblocked = () => reject(new Error("IndexedDB bloqueado (outra aba aberta?)"));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Falha ao abrir IndexedDB"));
  });
}

function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request error"));
  });
}

function promisifyTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB tx error"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB tx aborted"));
  });
}

// ─── API pública ─────────────────────────────────────────────────────────────

/**
 * Persiste uma ação na fila. Retorna o `id` gerado (UUID v4) — o caller pode
 * usar pra correlacionar com um item otimista na UI (ex.: bolha de msg pendente
 * cujo `id` == este id, removida quando o flush reconcilia).
 */
export async function enqueue<P>(type: OfflineActionType, payload: P): Promise<string> {
  const id = generateId();
  const action: OfflineAction<P> = {
    id,
    type,
    payload,
    attempt: 0,
    next_retry_at: Date.now(),
    enqueued_at: nextEnqueuedAt(),
  };
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readwrite");
    tx.objectStore(STORE_ACTIONS).add(action);
    await promisifyTx(tx);
    notify();
    return id;
  } finally {
    db.close();
  }
}

/** Todas as ações, em ordem FIFO (`enqueued_at` ASC) — preserva ordem de envio. */
export async function getAll(): Promise<OfflineAction[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readonly");
    const all = (await promisifyRequest(tx.objectStore(STORE_ACTIONS).getAll())) as OfflineAction[];
    all.sort((a, b) => a.enqueued_at - b.enqueued_at);
    return all;
  } finally {
    db.close();
  }
}

export async function getCount(): Promise<number> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readonly");
    return await promisifyRequest(tx.objectStore(STORE_ACTIONS).count());
  } finally {
    db.close();
  }
}

export async function remove(id: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readwrite");
    tx.objectStore(STORE_ACTIONS).delete(id);
    await promisifyTx(tx);
    notify();
  } finally {
    db.close();
  }
}

/**
 * Delay (ms) entre falha e próxima tentativa. `failureCount` = tentativas JÁ
 * falhadas (0 = primeira falha). Schedule: 1s, 4s, 16s, 64s→saturado em 60s.
 */
export function computeBackoffDelay(failureCount: number): number {
  const exp = Math.min(Math.max(failureCount, 0), 10);
  const delay = BACKOFF_BASE_MS * Math.pow(4, exp);
  return Math.min(delay, BACKOFF_MAX_MS);
}

export async function incrementAttempt(id: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readwrite");
    const store = tx.objectStore(STORE_ACTIONS);
    const existing = (await promisifyRequest(store.get(id))) as OfflineAction | undefined;
    if (!existing) {
      tx.abort();
      return;
    }
    const updated: OfflineAction = {
      ...existing,
      attempt: existing.attempt + 1,
      next_retry_at: Date.now() + computeBackoffDelay(existing.attempt),
    };
    store.put(updated);
    await promisifyTx(tx);
  } finally {
    db.close();
  }
}

/**
 * Esvazia a fila. SEGURANÇA: chamar no logout — device compartilhado não pode
 * vazar ações pendentes do usuário anterior.
 */
export async function clearAll(): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_ACTIONS, "readwrite");
    tx.objectStore(STORE_ACTIONS).clear();
    await promisifyTx(tx);
    notify();
  } finally {
    db.close();
  }
}

/**
 * Drena a fila percorrendo as ações em ordem FIFO (`enqueued_at` ASC) e chamando
 * `handlers[type]`:
 *
 * - resolve              → remove da fila (`sent++`)
 * - PermanentActionError → remove + warn (`failed++`)  [4xx não-retryable]
 * - outro throw          → incrementa attempt + backoff (`failed++`)  [retry]
 *   - exceto se atingiu `MAX_FLUSH_ATTEMPTS` → dead-letter (remove + warn)
 * - sem handler          → mantém na fila + warn (não perde a ação)
 *
 * **Ordem por-tipo (FIFO):** assim que uma ação de um tipo não pode avançar
 * (não-elegível por backoff, sem handler, ou falha retryable), bloqueamos os
 * itens POSTERIORES do MESMO tipo nesta passada. Sem isso, msg #2 poderia ser
 * enviada antes da msg #1 (que falhou/está em backoff) → entrega fora de ordem.
 * Tipos diferentes (mark_read, react) não se bloqueiam entre si.
 *
 * **Re-entrância:** lock a nível de lib — uma 2ª chamada concorrente retorna
 * `{sent:0,failed:0}` em vez de re-processar itens ainda não removidos.
 */
export async function flush(handlers: HandlerMap): Promise<{ sent: number; failed: number }> {
  if (flushing) return { sent: 0, failed: 0 };
  flushing = true;
  try {
    const all = await getAll();
    const now = Date.now();
    let sent = 0;
    let failed = 0;
    // Tipos cujo item anterior (em ordem FIFO) não avançou nesta passada.
    const blockedTypes = new Set<OfflineActionType>();

    for (const action of all) {
      if (blockedTypes.has(action.type)) continue;

      // Ainda em backoff: bloqueia este tipo pra preservar ordem (não envia o
      // próximo do mesmo tipo antes deste).
      if (action.next_retry_at > now) {
        blockedTypes.add(action.type);
        continue;
      }

      const handler = handlers[action.type];
      if (!handler) {
        console.warn("[offline-queue] sem handler registrado pro tipo:", action.type);
        blockedTypes.add(action.type);
        continue;
      }

      try {
        await handler(action.payload, action);
        await remove(action.id);
        sent += 1;
      } catch (err) {
        if (err instanceof PermanentActionError) {
          console.warn(
            "[offline-queue] descartando ação não-retryable:",
            action.type,
            action.id,
            err.message
          );
          await remove(action.id);
          failed += 1;
        } else if (action.attempt + 1 >= MAX_FLUSH_ATTEMPTS) {
          // Dead-letter: esgotou as tentativas. Descarta pra não travar o tipo.
          console.warn(
            "[offline-queue] dead-letter após",
            MAX_FLUSH_ATTEMPTS,
            "tentativas:",
            action.type,
            action.id
          );
          await remove(action.id);
          failed += 1;
        } else {
          console.debug("[offline-queue] retry por erro retryable:", action.type, err);
          await incrementAttempt(action.id);
          failed += 1;
          blockedTypes.add(action.type); // não drena itens posteriores do mesmo tipo
        }
      }
    }
    return { sent, failed };
  } finally {
    flushing = false;
  }
}
